/**
 * Projectile render manager — owns the per-projectile Mesh map; reconciles
 * additions/removals from the schema's projectiles MapSchema each frame.
 *
 * FoW-gated on the server: projectiles are @view-gated, so culled projectiles
 * disappear from room.state.projectiles and the seen-set diff in reconcile()
 * despawns their meshes — exactly like creatures.
 *
 * Visual: a low-poly arrow/bolt (shaft + head, flat team-tinted) oriented along
 * its travel direction. Y is lifted by ARC_HEIGHT * sin(progress * π) so it
 * follows a parabolic arc from spawn point to target, and the mesh is pitched to
 * the arc tangent (nose-up climbing, nose-down on descent). Position-lerped at
 * POS_LERP each frame.
 */

import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import type { Projectile } from "@tribez/shared";

/** Minimal structural interface covering the MapSchema API we actually use. */
interface ProjectileMap {
  forEach(cb: (value: Projectile, key: string) => void): void;
}

// Snappier than creatures — projectiles travel fast and lerp lag is visible.
const POS_LERP = 0.4;
// Peak arc lift (world units) at mid-flight.
const ARC_HEIGHT = 0.6;
// Height above terrain for the bolt mesh.
const BOLT_OFFSET_Y = 0.8;

const ALLY_COLOR  = 0x66ccff;
const ENEMY_COLOR = 0xff8844;

// The arrow geometry is built pointing along +Z (see buildArrowGeometry); we
// rotate it each frame from this forward axis onto the travel direction.
const FORWARD = new THREE.Vector3(0, 0, 1);
// Below this per-frame travel distance (squared) we keep the previous
// orientation (avoids jitter when a projectile is momentarily stationary).
const MIN_TURN_DIST_SQ = 1e-8;

// Reused scratch vectors — no per-frame allocation in reconcile().
const _prev = new THREE.Vector3();
const _dir = new THREE.Vector3();

type Entry = { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial; targetX: number; targetZ: number };

export interface ProjectileRenderManager {
  /** Sync the projectiles map from a Colyseus state snapshot. */
  reconcile(map: ProjectileMap): void;
  /** Remove a single projectile mesh by id (e.g. on impact). */
  remove(id: string): void;
  /** Dispose all meshes + shared geometry. */
  dispose(): void;
}

/**
 * Low-poly arrow centred on the origin, pointing +Z (tip at +Z). Built along +Y
 * (cylinder/cone three.js default axis) then rotated onto +Z. Centring on the
 * origin keeps the projectile's logical point at the arrow's middle.
 */
function buildArrowGeometry(): THREE.BufferGeometry {
  const SHAFT_LEN = 0.5;
  const HEAD_LEN = 0.22;
  const TOTAL = SHAFT_LEN + HEAD_LEN;

  const shaft = new THREE.CylinderGeometry(0.028, 0.028, SHAFT_LEN, 6);
  shaft.translate(0, SHAFT_LEN / 2, 0); // base at y=0, top at y=SHAFT_LEN

  const head = new THREE.ConeGeometry(0.075, HEAD_LEN, 6);
  head.translate(0, SHAFT_LEN + HEAD_LEN / 2, 0); // base at shaft top, tip at y=TOTAL

  const merged = mergeGeometries([shaft, head], false)!;
  shaft.dispose();
  head.dispose();

  merged.translate(0, -TOTAL / 2, 0); // centre on origin along the build axis (Y)
  merged.rotateX(Math.PI / 2);        // +Y forward → +Z forward
  return merged;
}

export function createProjectileRender(
  scene: THREE.Scene,
  sampleHeight: (x: number, z: number) => number,
  localTeamId: () => number,
): ProjectileRenderManager {
  const meshes = new Map<string, Entry>();
  // Shared geometry — created once, disposed in dispose().
  const geo = buildArrowGeometry();

  function spawn(p: Projectile): void {
    const isAlly = p.teamId === localTeamId();
    const mat = new THREE.MeshBasicMaterial({ color: isAlly ? ALLY_COLOR : ENEMY_COLOR });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(p.x, sampleHeight(p.x, p.z) + BOLT_OFFSET_Y, p.z);
    // Initial heading toward the target (horizontal) so the first frame isn't sideways.
    _dir.set(p.x - p.spawnX, 0, p.z - p.spawnZ);
    if (_dir.lengthSq() > MIN_TURN_DIST_SQ) {
      mesh.quaternion.setFromUnitVectors(FORWARD, _dir.normalize());
    }
    scene.add(mesh);
    meshes.set(p.id, { mesh, mat, targetX: p.x, targetZ: p.z });
  }

  function remove(id: string): void {
    const e = meshes.get(id);
    if (!e) return;
    e.mat.dispose();
    scene.remove(e.mesh);
    meshes.delete(id);
  }

  return {
    reconcile(map: ProjectileMap) {
      const seen = new Set<string>();

      map.forEach((p: Projectile, id: string) => {
        seen.add(id);
        let e = meshes.get(id);
        if (!e) {
          spawn(p);
          e = meshes.get(id)!;
        }

        // Update lerp target from latest server position.
        e.targetX = p.x;
        e.targetZ = p.z;

        // Lerp toward target (POS_LERP per frame — called at frame cadence).
        _prev.copy(e.mesh.position);
        const px = _prev.x + (p.x - _prev.x) * POS_LERP;
        const pz = _prev.z + (p.z - _prev.z) * POS_LERP;

        // Parabolic arc: lift by ARC_HEIGHT * sin(progress * π) where progress
        // is the fraction of the spawn→target distance already covered.
        const totalDist = Math.hypot(p.x - p.spawnX, p.z - p.spawnZ) || 1;
        const flownDist = Math.hypot(px - p.spawnX, pz - p.spawnZ);
        const prog = Math.min(1, flownDist / totalDist);
        const arcY = ARC_HEIGHT * Math.sin(prog * Math.PI);
        const py = sampleHeight(px, pz) + BOLT_OFFSET_Y + arcY;

        // Orient to the full 3D travel delta (includes the arc tangent → nose
        // pitches up climbing, down descending). Keep prior heading if ~still.
        _dir.set(px - _prev.x, py - _prev.y, pz - _prev.z);
        if (_dir.lengthSq() > MIN_TURN_DIST_SQ) {
          e.mesh.quaternion.setFromUnitVectors(FORWARD, _dir.normalize());
        }

        e.mesh.position.set(px, py, pz);
      });

      // Remove meshes whose projectile is no longer in the schema (FoW cull or impact).
      for (const id of [...meshes.keys()]) {
        if (!seen.has(id)) remove(id);
      }
    },

    remove,

    dispose() {
      for (const id of [...meshes.keys()]) remove(id);
      geo.dispose();
    },
  };
}
